/**
 * The template library: what we ship, and what this office has written.
 *
 * Two sections rather than one list, because they answer different questions.
 * The presets are starting points that arrive with a deploy and cannot be
 * deleted; the saved ones are somebody's actual wording, with a count of the
 * campaigns that started from them.
 *
 * Every card shows a real rendering of the letter rather than a stock
 * thumbnail. Building it is one call to the same renderer the mail uses, and a
 * picker that shows four identical grey rectangles is a picker nobody uses
 * twice.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LayoutTemplate, Loader2, Plus, Sparkles, Trash2 } from "lucide-react";
import { Button, Loader } from "@wa/ui";
import { useT, useI18n } from "../../../../i18n/I18nProvider";
import { IconBtn } from "../../../ui";
import { userCanPlatform, type StaffUser } from "../../../../lib/auth";
import { deleteTemplate, listTemplates, type TemplateRow } from "../../../../lib/marketingTemplates";
import { type PresetLocale } from "../../../../emailBuilder";
import { Note } from "../shared";
import { PresetShelf } from "./PresetShelf";
import { TemplateCard } from "./TemplateCard";

export function TemplatesList({ user }: { user: StaffUser }) {
  const t = useT();
  const { locale } = useI18n();
  const navigate = useNavigate();
  const canManage = userCanPlatform(user, "manage:crm.marketing");

  const [rows, setRows] = useState<TemplateRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  // The presets are written in both languages; the interface language picks
  // which one is offered, since an operator writing in Estonian wants the
  // Estonian wording as their starting point.
  const presetLocale: PresetLocale = locale === "en" ? "en" : "et";

  const load = useCallback(async () => {
    try {
      const res = await listTemplates();
      setRows(res.rows);
      setError(null);
    } catch {
      setRows([]);
      setError(t("marketing.error.load"));
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  async function remove(row: TemplateRow) {
    if (!window.confirm(t("marketing.templates.confirmDelete", { name: row.name }))) return;
    setBusy(row.id);
    try {
      await deleteTemplate(row.id);
      setRows((prev) => (prev ?? []).filter((r) => r.id !== row.id));
      setError(null);
    } catch {
      setError(t("marketing.templates.deleteFailed"));
    } finally {
      setBusy(null);
    }
  }

  // An edited shipped letter is shown on the shelf above, in the place of the
  // letter it replaces. Listing it here as well would be the same template
  // twice, and only one of the two would carry "restore the original".
  const saved = (rows ?? []).filter((r) => !r.presetId);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <LayoutTemplate className="size-5 text-primary" />
          <div>
            <h1 className="text-lg font-semibold tracking-tight">{t("marketing.templates.title")}</h1>
            <p className="text-xs text-muted-foreground">{t("marketing.templates.intro")}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" asChild>
            <Link to="/marketing/campaigns">{t("nav.campaigns")}</Link>
          </Button>
          {canManage && (
            <Button size="sm" asChild>
              <Link to="/marketing/templates/new">
                <Plus className="size-4" /> {t("marketing.templates.new")}
              </Link>
            </Button>
          )}
        </div>
      </header>

      {error && <Note tone="bad">{error}</Note>}

      <section className="space-y-3">
        <div className="flex items-center gap-2">
          <Sparkles className="size-4 text-primary" />
          <h2 className="text-sm font-semibold tracking-tight">{t("marketing.templates.presetsTitle")}</h2>
        </div>
        <p className="text-xs text-muted-foreground">{t("marketing.templates.presetsIntro")}</p>
        <PresetShelf
          rows={rows ?? []}
          presetLocale={presetLocale}
          canManage={canManage}
          onChanged={() => void load()}
          onError={(key) => setError(t(key))}
        />
      </section>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold tracking-tight">{t("marketing.templates.savedTitle")}</h2>
        {rows === null ? (
          <Loader />
        ) : saved.length === 0 ? (
          <p className="rounded-xl border border-dashed border-border px-4 py-10 text-center text-sm text-muted-foreground">
            {t("marketing.templates.empty")}
          </p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {saved.map((row) => (
              <TemplateCard
                key={row.id}
                to={`/marketing/templates/${row.id}`}
                name={row.name}
                description={row.description ?? ""}
                subject={row.subject}
                locale={row.locale}
                usedBy={row.usedBy}
                updatedAt={row.updatedAt}
                design={row.design}
                actions={
                  // An icon, like the shelf above it. A worded Delete was the
                  // only text on these cards and it read as the card's purpose.
                  canManage && (
                    <IconBtn
                      small
                      disabled={busy === row.id}
                      title={t("common.delete")}
                      aria-label={t("common.delete")}
                      onClick={() => void remove(row)}
                    >
                      {busy === row.id ? (
                        <Loader2 className="size-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="size-3.5" />
                      )}
                    </IconBtn>
                  )
                }
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
